import { Box, Button, Dialog, IconButton, MenuItem, Select, Typography, Stack, Tooltip, Divider, Fab, DialogContent, InputAdornment, TextField } from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { GridRowId, GridRowsProp, GridColDef } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers";
import { useParams } from "react-router";
import axios from "axios";

// Icons
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Logic & Tools
import getFeesData from "../../../tools/forecast/getFeesData";
import { IFees } from "../../../interface/fees";
import getForecastMonthList from "../../../tools/forecast/getForecastMonthList";
import { addFee, handleUpdateFee } from "../../../tools/forecast/addUpdateFees";
import { showSnackbar } from "../../../redux/actions";
import { setTotalByMonth } from "../../../redux/actions/forecastActions";

import { urlAPI } from "../../../config/config";

// Components
import AddUpdateFee from "../AddUpdate/AddUpdateFee";
import AlerteDialog from "../../AlerteDialog/AlerteDialog";
import CustomDataGrid from "../../customDataGrid/CustomDataGrid";

const Fees = ({ forecastVersion, forecastDates }: { forecastVersion: number | undefined, forecastDates: string }) => {
    const dispatch = useDispatch();
    const params = useParams();
    const token = useSelector((state: RootState) => state.user.token);

    const [feesData, setFeesData] = useState<IFees[]>([]);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [selectedFee, setSelectedFee] = useState<IFees | null>(null);
    const [selectedFees, setSelectedFees] = useState<GridRowId[]>([]);
    const [edit, setEdit] = useState<boolean>(false);
    const [searchLabel, setSearchLabel] = useState<string>("");
    const [agency, setAgency] = useState<string>("All");
    
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);

    const monthList = useMemo(() => getForecastMonthList(forecastDates), [forecastDates]);

    useEffect(() => {
        getFeesData(params.id, forecastVersion, agency, setFeesData, token, dispatch, () => {});
    }, [agency, forecastVersion, openModal, showDeleteDialog]);

    // --- CONFIGURATION DES COLONNES ---
    const columns: GridColDef[] = [
        { 
            field: "label", 
            headerName: "Libellé / Bénéficiaire", 
            minWidth: 300, 
            flex: 1,
            renderCell: (params: any) => (
                <Box sx={{ py: 1, lineHeight: '1.2' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                        {params.row.label}
                    </Typography>
                    {/* Affichage du bénéficiaire en dessous du libellé */}
                    <Typography variant="caption" sx={{ color: "#64748b", display: 'block', mt: 0.5 }}>
                        👤 {params.row.beneficiaryName || "Aucun bénéficiaire"}
                    </Typography>
                </Box>
            )
        },
        ...monthList.map((m) => ({
            field: m.field,
            headerName: m.name,
            minWidth: 110,
            align: "right" as const,
            headerAlign: "right" as const,
        }))
    ];

    // --- PRÉPARATION DES LIGNES (rows) ---
    const rows = useMemo(() => {
    // 1. Filtrage (Libellé ou Bénéficiaire)
    const filtered = feesData.filter(f => 
        f.label.toLowerCase().includes(searchLabel.toLowerCase()) ||
        (f.beneficiary?.firstname || "").toLowerCase().includes(searchLabel.toLowerCase()) ||
        (f.beneficiary?.lastname || "").toLowerCase().includes(searchLabel.toLowerCase())
    );

    // 2. Transformation des données pour le DataGrid
    const dataRows = filtered.map((fee) => {
        const monthValues = Object.fromEntries(
            monthList.map(m => {
                // IMPORTANT : On cherche dans les valeurs de fee.months l'objet qui a le bon label
                const monthEntry: any = Object.values(fee.months || {}).find(
                    (entry: any) => entry.label === m.field
                );
                
                // Si trouvé on prend .value, sinon on regarde si c'est une valeur directe, sinon 0
                const val = monthEntry ? (monthEntry.value ?? monthEntry) : 0;
                return [m.field, `${Number(val).toLocaleString()} €`];
            })
        );

        return { 
            id: fee._id, 
            label: fee.label, 
            beneficiaryName: fee.beneficiary ? `${fee.beneficiary.firstname} ${fee.beneficiary.lastname}` : "",
            ...monthValues 
        };
    });

    // 3. Calcul de la ligne de Total
    const totalRowValues = Object.fromEntries(
        monthList.map(m => {
            const total = filtered.reduce((sum, fee) => {
                const monthEntry: any = Object.values(fee.months || {}).find(
                    (entry: any) => entry.label === m.field
                );
                const val = monthEntry ? (monthEntry.value ?? monthEntry) : 0;
                return sum + (Number(val) || 0);
            }, 0);
            return [m.field, `${total.toLocaleString()} €`];
        })
    );

    // 4. Mise à jour du store Redux pour les compteurs globaux
    const totalByMonthRedux = Object.fromEntries(
        monthList.map(m => {
            const total = filtered.reduce((sum, fee) => {
                const monthEntry: any = Object.values(fee.months || {}).find(
                    (entry: any) => entry.label === m.field
                );
                const val = monthEntry ? (monthEntry.value ?? monthEntry) : 0;
                return sum + (Number(val) || 0);
            }, 0);
            return [m.field, total];
        })
    );
    dispatch(setTotalByMonth("Frais de fonctionnement", totalByMonthRedux));

    return [...dataRows, { id: "total", label: "TOTAL GÉNÉRAL", beneficiaryName: "", ...totalRowValues }];
}, [feesData, searchLabel, monthList, dispatch]);

    // --- ACTIONS ---
    const confirmDelete = async () => {
        try {
            await axios.delete(`${urlAPI}/fee/`, {
                headers: { Authorization: `Bearer ${token}` },
                data: { selectedId: selectedFees },
            });
            setSelectedFees([]);
            setShowDeleteDialog(false);
            dispatch(showSnackbar("Frais supprimés", "success"));
        } catch (error) { dispatch(showSnackbar("Erreur", "error")); }
    };

    return (
        <Box sx={{ p: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
            
            {/* --- HEADER COHÉRENT --- */}
            <Box sx={{ bgcolor: "white", p: 3, borderRadius: 4, mb: 4, border: "1px solid #e2e8f0", boxShadow: "0px 4px 20px rgba(0,0,0,0.03)" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>📂 Frais de fonctionnement</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: "#64748b", bgcolor: "#f1f5f9", px: 1.5, py: 0.5, borderRadius: 1 }}>V{forecastVersion}</Typography>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                    <Select value={agency} onChange={(e) => setAgency(e.target.value)} size="small" sx={{ minWidth: 160, borderRadius: 2, bgcolor: "#f1f5f9", "& fieldset": { border: "none" }, fontWeight: 600 }}>
                        <MenuItem value="All">Toutes les agences</MenuItem>
                        <MenuItem value="Nord">Agence Nord</MenuItem>
                        <MenuItem value="Sud">Agence Sud</MenuItem>
                    </Select>

                    <TextField 
                        size="small" placeholder="Rechercher..." value={searchLabel} onChange={(e) => setSearchLabel(e.target.value)}
                        sx={{ bgcolor: "#f1f5f9", borderRadius: 2, "& fieldset": { border: "none" }, minWidth: 250 }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="disabled"/></InputAdornment> }}
                    />

                    <Box sx={{ flexGrow: 1 }} />
                    <Button variant="contained" startIcon={<AddCircleIcon />} onClick={() => { setEdit(false); setSelectedFee(null); setOpenModal(true); }} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, px: 3 }}>Ajouter un frais</Button>
                </Box>
            </Box>

            {/* --- TABLEAU AVEC CUSTOMDATAGRID (Cohérence TNS) --- */}
            <Box sx={{ mb: 12 }}>
                <CustomDataGrid
                    rows={rows}
                    columns={columns}
                    getRowHeight={() => "auto"}
                    checkboxSelection
                    rowSelectionModel={selectedFees}
                    onRowSelectionModelChange={(newSelection) => setSelectedFees(newSelection)}
                    isRowSelectable={(params: any) => params.row.id !== 'total'}
                    onCellClick={(params: any) => {
                        if (params.row.id !== 'total' && params.field !== "__check__") {
                            const rowData = feesData.find(f => f._id === params.row.id);
                            if (rowData) { 
                                setEdit(true); 
                                setSelectedFee(rowData); 
                                setOpenModal(true); 
                            }
                        }
                    }}
                    // On retire getRowClassName qui cause l'erreur et on fait tout dans le sx
                    sx={{
                        // Cible spécifiquement la ligne qui a l'ID "total"
                        '& .MuiDataGrid-row[data-id="total"]': {
                            bgcolor: '#f1f5f9 !important',
                            fontWeight: '800 !important',
                            pointerEvents: 'none', // Empêche le hover et le clic
                            '& .MuiDataGrid-cell': {
                                color: '#0f172a',
                                borderTop: '2px solid #e2e8f0', // Petite bordure pour séparer le total
                            },
                            // Cache la checkbox sur la ligne de total
                            '& .MuiDataGrid-checkboxInput': {
                                display: 'none',
                            }
                        },
                        // Optionnel : style pour les autres lignes pour correspondre à "Trouver"
                        '& .MuiDataGrid-row:hover': {
                            bgcolor: '#f8fafc',
                        }
                    }}
                />
            </Box>

            {/* --- DOCK FLOTTANT --- */}
            <Box sx={{ position: "fixed", bottom: 30, right: "50%", transform: "translateX(50%)", display: "flex", alignItems: "center", gap: 2, bgcolor: "white", p: "12px 24px", borderRadius: "100px", border: "1px solid #e2e8f0", boxShadow: "0px 10px 40px rgba(0,0,0,0.1)", zIndex: 1100 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mr: 1 }}>{selectedFees.length} sélectionnés</Typography>
                <Tooltip title="Supprimer"><Fab color="error" size="small" disabled={selectedFees.length === 0} onClick={() => setShowDeleteDialog(true)}><DeleteIcon fontSize="small" /></Fab></Tooltip>
                <Divider orientation="vertical" flexItem />
                <Fab color="primary" size="medium" onClick={() => { setEdit(false); setSelectedFee(null); setOpenModal(true); }}><AddCircleIcon /></Fab>
            </Box>

            {/* MODALE ET ALERTES (Inchangées pour la logique) */}
            <Dialog open={openModal} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 4 } }}>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
                    <Typography variant="h6" sx={{fontWeight: 800}}>{edit ? "Modifier" : "Ajouter"} un frais</Typography>
                    <IconButton onClick={() => setOpenModal(false)}><CloseIcon /></IconButton>
                </Box>
                <DialogContent sx={{ p: 0 }}>
                    <AddUpdateFee handleSubmit={edit ? handleUpdateFee : addFee} forecastId={params.id} version={forecastVersion} setOpenModal={setOpenModal} monthList={monthList} initialValue={selectedFee || {}} isEdit={edit} />
                </DialogContent>
            </Dialog>

            <AlerteDialog open={showDeleteDialog} handleClose={() => setShowDeleteDialog(false)} deleteProject={confirmDelete} title="Supprimer ?" contentText="Voulez-vous supprimer les éléments sélectionnés ?" color="error" />
        </Box>
    );
};

export default Fees;